import { Button, Modal } from "@talladega/components";
import { useTranslation } from "next-i18next";
import WarrantyGetStartedImage from "@public/images/purchase/coverage/hero-resume-warranty.jpg";
import FinancingGetStartedImage from "@public/images/purchase/consumer/hero-resume-financing.jpg";
import LocalImage from "@common/components/LocalImage";
import { ArrowDiagonalUpRightIcon, ArrowRightIcon } from "@talladega/icons";
import { useRouter } from "next/router";
import {
  roadwiseFinancingDarkLogo,
  roadwiseWarrantyDarkLogo,
} from "@public/images";
import useAftermarketPurchase from "@modules/purchase/warranty/hooks/useAftermarketPurchase";
import { tryGetActiveAftermarketPurchase } from "@modules/purchase/warranty/types/Purchase";
import {
  aftermarketDetailsStep,
  getRemainingPurchaseSteps,
  getStepById,
  showResumeForStep,
  showResumeQuoteStep,
} from "@modules/purchase/PurchaseStep";
import DealCustomerTypeEnum from "@common/enums/DealCustomerTypeEnum";
import { getProvinceById } from "@talladega/types";
import { sources } from "@common/enums/SourceEnum";
import useConsumerFinancing from "@modules/purchase/consumer/hooks/useConsumerFinancing";
import { tryGetActiveConsumerFinancingPurchase } from "@modules/purchase/consumer/types/ConsumerFinancing";
import {
  getStepById as consumerGetStepById,
  getStepPathname,
} from "@modules/purchase/consumer/types/ConsumerFinancingStep";

type Props = {
  onClose: () => void;
  isOpen: boolean;
};

const RoadwiseResumeStartPurchaseModal = ({ isOpen, onClose }: Props) => {
  const { t } = useTranslation(["purchase", "common"]);
  const router = useRouter();
  const locale = router.locale || "en";

  const { purchase: purchaseResponse } = useAftermarketPurchase();
  const aftermarketPurchase = tryGetActiveAftermarketPurchase(purchaseResponse);

  const { consumerFinancing: consumerFinancingOrNotFound } =
    useConsumerFinancing();

  const consumerFinancingPurchase = tryGetActiveConsumerFinancingPurchase(
    consumerFinancingOrNotFound
  );

  const mainApplicant = aftermarketPurchase?.customers.find(
    (c) => c.typeId === DealCustomerTypeEnum.MainApplicant
  );

  const provinceAbbr = mainApplicant?.currentAddress?.provinceId
    ? getProvinceById(mainApplicant.currentAddress.provinceId)?.abbr
    : "";

  const odometer = aftermarketPurchase?.vehicle?.odometerCount;
  const vin = aftermarketPurchase?.vehicle?.vin;
  const year = aftermarketPurchase?.vehicle?.year;
  const make = aftermarketPurchase?.vehicle?.make;
  const model = aftermarketPurchase?.vehicle?.model;

  const hasValidVehicleInfo = !!vin || (!!year && !!make && !!model);
  const hasProvince = !!provinceAbbr;
  const hasOdometer = !!odometer;

  const showAftermarketResume =
    !!aftermarketPurchase &&
    showResumeForStep(aftermarketPurchase.currentStepId);
  const showAftermarketResumeQuote =
    !!aftermarketPurchase &&
    showResumeQuoteStep(aftermarketPurchase.currentStepId) &&
    hasValidVehicleInfo &&
    hasProvince &&
    hasOdometer;

  const aftermarketPurchaseStep = getStepById(
    "aftermarket",
    aftermarketPurchase?.currentStepId ?? aftermarketDetailsStep.id
  );

  const handleOnResumeWarrantyPurchaseClick = () => {
    if (!aftermarketPurchase) return;

    if (aftermarketPurchaseStep) {
      router.push({ pathname: aftermarketPurchaseStep.href });
      onClose();
    }
  };

  const remainingAftermarketSteps = getRemainingPurchaseSteps(
    "aftermarket",
    aftermarketPurchaseStep
  );

  const handleOnResumeWarrantyQuoteClick = () => {
    if (!aftermarketPurchase) return;
    if (hasValidVehicleInfo && hasProvince && hasOdometer) {
      const quoteParams: Record<string, string> = {
        source:
          sources
            .find((s) => s.name === aftermarketPurchase.dealSource.toString())
            ?.name.toLowerCase() || "",
        odometer: odometer?.toString() || "",
        province: provinceAbbr,
        removeDiscount: "true",
      };

      if (vin) {
        quoteParams.vin = vin;
      } else {
        quoteParams.year = year?.toString() || "";
        quoteParams.make = make || "";
        quoteParams.model = model || "";
        quoteParams.trim = aftermarketPurchase?.vehicle?.trim || "";
      }

      router.push(
        `/warranty/quote/?${new URLSearchParams(quoteParams).toString()}`
      );
      onClose();
    }
  };

  const financingPurchaseStep =
    consumerFinancingPurchase?.currentStepId != null
      ? consumerGetStepById(consumerFinancingPurchase.currentStepId)
      : null;

  const showConsumerFinancingResume =
    !!consumerFinancingPurchase && !!financingPurchaseStep;

  const handleOnResumeConsumerPurchaseClick = () => {
    if (
      financingPurchaseStep &&
      router.pathname !== financingPurchaseStep.href
    ) {
      router.push({
        pathname: getStepPathname(financingPurchaseStep),
        query: { ...router.query }, // preserves current query
      });
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      modalType="none"
      alignment="center"
      size="4xl"
      title={t("common:get_started")}
    >
      <div className="w-full max-w-6xl mx-auto">
        <div className="mx-auto mb-4 flex flex-col gap-2 text-center">
          <span className="text-primary font-bold text-2xl">
            {t("purchase:were_here_for_you_whatever_you_need")}
          </span>
          <span className="text-2xl text-tertiary lg:px-20">
            {t("purchase:are_you_thinking_about_financing_a_new_car")}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-2xl shadow flex flex-col">
            <div className="rounded-t-2xl overflow-hidden mb-4">
              <div className="relative h-48 md:h-56 lg:h-64 rounded-t-2xl overflow-hidden mb-4 bg-gray-200">
                <LocalImage
                  src={FinancingGetStartedImage}
                  alt="Financing Get Started Image"
                  fill
                  priority
                  className="object-cover"
                  sizes="(min-width:1024px) 32rem, (min-width:768px) 50vw, 100vw"
                />
              </div>
            </div>
            <div className="flex-1 p-4">
              <LocalImage
                className="w-[95px] h-[40px] mb-3"
                width="95"
                height="40"
                src={
                  locale === "en"
                    ? roadwiseFinancingDarkLogo.srcEn
                    : roadwiseFinancingDarkLogo.srcFr
                }
                alt="Roadwise Financing Logo"
                priority
              />
              <h3 className="text-primary text-lg text-left font-semibold mb-1">
                {t("purchase:lets_get_you_that_dream_car")}
              </h3>
              <p className="text-sm text-left text-tertiary mb-4">
                {t("purchase:fast_easy_financing")}
              </p>
            </div>
            <div className="flex flex-col md:flex-row gap-2 p-4 mt-auto items-stretch md:items-center financing">
              {!consumerFinancingPurchase && (
                <Button
                  onClick={() => {
                    router.push("/financing");
                    onClose();
                  }}
                  className="w-full md:w-auto"
                >
                  {t("purchase:get_pre_approved_loan")}
                </Button>
              )}
              {showConsumerFinancingResume && (
                <Button
                  rightIcon={<ArrowRightIcon />}
                  onClick={handleOnResumeConsumerPurchaseClick}
                  className="w-full md:w-auto"
                >
                  {t("purchase:resume_purchase")}
                </Button>
              )}

              <div className="px-0 md:px-1 w-full md:w-auto">
                <Button
                  fill="link"
                  buttonStyle="borderless"
                  spacing="no-padding"
                  size="small"
                  rightIcon={
                    <ArrowDiagonalUpRightIcon className="h-9 w-9 text-primary-600" />
                  }
                  className="w-full md:w-auto text-primary-600"
                  onClick={() => {
                    router.push("/financing/how-it-works");
                    onClose();
                  }}
                >
                  {t("common:learn_more")}
                </Button>
              </div>
            </div>
          </div>

          {/* Warranty Card (with resume buttons) */}
          <div className="bg-gray-50 rounded-2xl shadow flex flex-col">
            <div className="rounded-t-2xl overflow-hidden mb-4">
              <div className="relative h-48 md:h-56 lg:h-64 rounded-t-2xl overflow-hidden mb-4 bg-gray-200">
                <LocalImage
                  src={WarrantyGetStartedImage}
                  alt="Warranty Get Started Image"
                  fill
                  priority
                  className="object-cover"
                  sizes="(min-width:1024px) 32rem, (min-width:768px) 50vw, 100vw"
                />
              </div>
            </div>
            <div className="flex-1 p-4">
              <LocalImage
                className="w-[95px] h-[40px] mb-3"
                width="95"
                height="40"
                src={
                  locale === "en"
                    ? roadwiseWarrantyDarkLogo.srcEn
                    : roadwiseWarrantyDarkLogo.srcFr
                }
                alt="Roadwise Warranty Logo"
                priority
              />
              {!showAftermarketResumeQuote && remainingAftermarketSteps > 0 ? (
                <>
                  <h3 className="text-primary text-lg text-left font-semibold mb-1">
                    {t("purchase:youre_almost_covered")}
                  </h3>
                  <p className="text-sm text-left text-tertiary mb-4">
                    {t("purchase:youve_made_great_progress_steps", {
                      value: remainingAftermarketSteps,
                      stepsLabel:
                        remainingAftermarketSteps > 1
                          ? t("common:steps")
                          : t("common:step"),
                    })}
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-primary text-lg text-left font-semibold mb-1">
                    {t("purchase:dont_let_repairs_catch_you_off_guard")}
                  </h3>
                  <p className="text-sm text-left text-tertiary mb-4">
                    {t("purchase:save_on_major_repairs")}
                  </p>
                </>
              )}
            </div>

            <div className="flex flex-col md:flex-row gap-2 p-4 mt-auto items-stretch md:items-center">
              {!aftermarketPurchase && (
                <Button
                  onClick={() => {
                    router.push("/warranty");
                    onClose();
                  }}
                  className="w-full md:w-auto"
                >
                  {t("purchase:get_a_warranty_quote")}
                </Button>
              )}

              {showAftermarketResume && (
                <Button
                  rightIcon={<ArrowRightIcon />}
                  onClick={handleOnResumeWarrantyPurchaseClick}
                  className="w-full md:w-auto"
                >
                  {t("purchase:resume_purchase")}
                </Button>
              )}
              {showAftermarketResumeQuote && (
                <Button
                  rightIcon={<ArrowRightIcon />}
                  onClick={handleOnResumeWarrantyQuoteClick}
                  className="w-full md:w-auto"
                >
                  {t("purchase:view_my_quote")}
                </Button>
              )}

              <div className="px-0 md:px-1 w-full md:w-auto">
                <Button
                  fill="link"
                  buttonStyle="borderless"
                  size="small"
                  rightIcon={
                    <ArrowDiagonalUpRightIcon className="h-9 w-9 text-primary-600" />
                  }
                  className="w-full md:w-auto !text-primary-600"
                  onClick={() => {
                    router.push("/warranty/how-it-works");
                    onClose();
                  }}
                >
                  {t("common:learn_more")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default RoadwiseResumeStartPurchaseModal;
